/**
 * Fetches transfer records for a conversation with source/destination details.
 * GET /conversations/:id/transfers — returns rich transfer history for the
 * transfer tab (mirrors KC2 layout).
 */
import { useEffect, useState } from 'react'
import { api } from '../../../api/client.js'

export interface TransferRecord {
  id: string
  transferType: string
  reason: string | null
  description: string | null
  isPrivate: boolean
  cc: string[]
  transferredAt: string
  source: {
    agentId: string | null
    agentName: string | null
    teamId: string | null
    teamName: string | null
    campaignId: string | null
    campaignName: string | null
    locationId: string | null
    locationName: string | null
    branchId: string | null
    branchName: string | null
    departmentId: string | null
    departmentName: string | null
  }
  destination: {
    agentId: string | null
    agentName: string | null
    teamId: string | null
    teamName: string | null
    campaignId: string | null
    campaignName: string | null
    locationId: string | null
    locationName: string | null
    branchId: string | null
    branchName: string | null
    departmentId: string | null
    departmentName: string | null
  }
}

export function useConversationTransfers(
  conversationId: string | null,
  refreshKey?: number,
): {
  transfers: TransferRecord[]
  loading: boolean
  refetch: () => void
} {
  const [transfers, setTransfers] = useState<TransferRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [refetchKey, setRefetchKey] = useState(0)

  useEffect(() => {
    if (!conversationId) {
      setTransfers([])
      return
    }
    let cancelled = false
    setLoading(true)
    api
      .get<{ transfers: TransferRecord[] }>(`/conversations/${conversationId}/transfers`)
      .then((r) => {
        if (!cancelled) {
          setTransfers(r.transfers ?? [])
        }
      })
      .catch(() => {
        if (!cancelled) setTransfers([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [conversationId, refetchKey, refreshKey])

  return {
    transfers,
    loading,
    refetch: () => setRefetchKey((k) => k + 1),
  }
}
